import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  TextField,
  TableFooter,
  TablePagination,
  Divider,
  Chip,
} from '@mui/material';
import { BrandOutlinedButton } from '../button';
import * as XLSX from 'xlsx';
import {
  formatCampaignDate,
  getPercentage,
  smsCounts,
  smsRecipientCount,
  capitalizeStatus,
} from '../../utils/campaignDashboardUtils';

const recipientRowStatus = (row) => {
  if (row.deliveryStatus) return row.deliveryStatus;
  if (row.sendStatus === 'success') return 'sent';
  if (row.sendStatus === 'failed') return 'failed';
  return row.sendStatus || 'unknown';
};

const SMSAnalyticsDialog = ({ campaign, open, onClose }) => {
  const [textFilters, setTextFilters] = useState({
    phone: '',
    sendStatus: '',
    deliveryStatus: '',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const recipients = useMemo(
    () => campaign?.recipientResults || [],
    [campaign],
  );

  const totalRecipients = smsRecipientCount(campaign);
  const counts = smsCounts(campaign);

  const filteredRecipients = recipients.filter((row) => {
    const phone = (row.to || '').toLowerCase();
    const send = (row.sendStatus || '').toLowerCase();
    const delivery = (row.deliveryStatus || '').toLowerCase();
    if (textFilters.phone && !phone.includes(textFilters.phone.toLowerCase())) {
      return false;
    }
    if (textFilters.sendStatus && !send.includes(textFilters.sendStatus.toLowerCase())) {
      return false;
    }
    if (
      textFilters.deliveryStatus &&
      !delivery.includes(textFilters.deliveryStatus.toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  const paginated = filteredRecipients.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const exportData = () => {
    if (!campaign) return;
    const summaryData = [
      {
        'Campaign Name': campaign.campaignName,
        'Date Created': formatCampaignDate(campaign.createdAt),
        Recipients: totalRecipients,
        Sent: counts.sent,
        Delivered: counts.delivered,
        Failed: counts.failed,
        Segments: campaign.totalSegments ?? 0,
        'Credits charged': campaign.creditsCharged ?? 0,
        Status: campaign.status,
      },
    ];
    const detailData = recipients.map((row) => ({
      Phone: row.to,
      'Send status': row.sendStatus,
      'Delivery status': row.deliveryStatus || '',
      'Send error': row.sendError || '',
      'Delivery error': row.deliveryError || '',
      'Delivered at': row.deliveredAt
        ? formatCampaignDate(row.deliveredAt)
        : '',
    }));
    const sheetData = [
      ...summaryData,
      {},
      {
        Phone: 'Phone',
        'Send status': 'Send status',
        'Delivery status': 'Delivery status',
        'Send error': 'Send error',
        'Delivery error': 'Delivery error',
        'Delivered at': 'Delivered at',
      },
      ...detailData,
    ];
    const worksheet = XLSX.utils.json_to_sheet(sheetData, { skipHeader: true });
    const workbook = XLSX.utils.book_new();
    const sheetName = (campaign.campaignName || 'SMS Campaign').slice(0, 31);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, `${sheetName}.xlsx`);
  };

  if (!campaign) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ fontWeight: 'bold' }}>SMS campaign performance</DialogTitle>
      <DialogContent>
        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
          <Chip label={campaign.campaignName || 'Unnamed'} variant="outlined" />
          <Chip label={capitalizeStatus(campaign.status)} color="primary" size="small" />
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Sender: {campaign.senderPhone} · Created:{' '}
          {formatCampaignDate(campaign.createdAt)}
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={3} my={2}>
          <Box textAlign="center">
            <Typography variant="caption" color="text.secondary">
              Recipients
            </Typography>
            <Typography variant="h6">{totalRecipients}</Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="caption" color="text.secondary">
              Sent
            </Typography>
            <Typography variant="h6">
              {counts.sent} ({getPercentage(counts.sent, totalRecipients)})
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="caption" color="text.secondary">
              Delivered
            </Typography>
            <Typography variant="h6">
              {counts.delivered} ({getPercentage(counts.delivered, totalRecipients)})
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="caption" color="text.secondary">
              Failed
            </Typography>
            <Typography variant="h6">
              {counts.failed} ({getPercentage(counts.failed, totalRecipients)})
            </Typography>
          </Box>
        </Box>
        <Typography
          variant="body1"
          sx={{ whiteSpace: 'pre-wrap', mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}
        >
          {campaign.message}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Send status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Delivery</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Error</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <TextField
                    value={textFilters.phone}
                    onChange={(e) =>
                      setTextFilters({ ...textFilters, phone: e.target.value })
                    }
                    placeholder="Filter"
                    variant="standard"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={textFilters.sendStatus}
                    onChange={(e) =>
                      setTextFilters({ ...textFilters, sendStatus: e.target.value })
                    }
                    placeholder="Filter"
                    variant="standard"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={textFilters.deliveryStatus}
                    onChange={(e) =>
                      setTextFilters({
                        ...textFilters,
                        deliveryStatus: e.target.value,
                      })
                    }
                    placeholder="Filter"
                    variant="standard"
                    size="small"
                  />
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No recipient details yet.
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((row) => (
                  <TableRow key={row.to}>
                    <TableCell>{row.to}</TableCell>
                    <TableCell>{row.sendStatus || '—'}</TableCell>
                    <TableCell>{recipientRowStatus(row)}</TableCell>
                    <TableCell>
                      {row.sendError || row.deliveryError || '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={filteredRecipients.length}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                  colSpan={4}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <BrandOutlinedButton onClick={exportData}>Export</BrandOutlinedButton>
        <BrandOutlinedButton onClick={onClose}>Close</BrandOutlinedButton>
      </DialogActions>
    </Dialog>
  );
};

export default SMSAnalyticsDialog;
